import type { PageSettings, PageSize, PageSizeName } from "../types/template.js";

/** CSS pixels per millimetre at 96 dpi. */
export const PX_PER_MM = 96 / 25.4;

export const PAGE_SIZES_MM: Record<PageSizeName, { width: number; height: number }> = {
  A3: { width: 297, height: 420 },
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  Letter: { width: 215.9, height: 279.4 },
  Legal: { width: 215.9, height: 355.6 },
  Tabloid: { width: 279.4, height: 431.8 }
};

export function mmToPx(mm: number): number {
  return Math.round(mm * PX_PER_MM * 100) / 100;
}

export function pxToMm(px: number): number {
  return Math.round((px / PX_PER_MM) * 100) / 100;
}

export function pageSizeMm(size: PageSize): { width: number; height: number } {
  return typeof size === "string" ? PAGE_SIZES_MM[size] ?? PAGE_SIZES_MM.A4 : size;
}

/** Concrete page geometry in CSS px. */
export interface PageGeometry {
  width: number;
  height: number;
  margins: { top: number; right: number; bottom: number; left: number };
  headerHeight: number;
  footerHeight: number;
  /** Width of the content column (page width minus horizontal margins). */
  contentWidth: number;
  /** Height available for body content on a regular page. */
  contentHeight: number;
  /** Y offset of the body content box from the page top. */
  contentTop: number;
  /** Y offset of the header region from the page top. */
  headerTop: number;
  /** Y offset of the footer region from the page top. */
  footerTop: number;
}

export function computePageGeometry(page: PageSettings): PageGeometry {
  const size = pageSizeMm(page.size);
  const portrait = page.orientation !== "landscape";
  const width = mmToPx(portrait ? size.width : size.height);
  const height = mmToPx(portrait ? size.height : size.width);
  const margins = {
    top: mmToPx(page.margins.top),
    right: mmToPx(page.margins.right),
    bottom: mmToPx(page.margins.bottom),
    left: mmToPx(page.margins.left)
  };
  const headerHeight = mmToPx(Math.max(0, page.header.height));
  const footerHeight = mmToPx(Math.max(0, page.footer.height));
  const contentTop = margins.top + headerHeight;
  const footerTop = height - margins.bottom - footerHeight;
  return {
    width,
    height,
    margins,
    headerHeight,
    footerHeight,
    contentWidth: Math.max(0, width - margins.left - margins.right),
    contentHeight: Math.max(0, footerTop - contentTop),
    contentTop,
    headerTop: margins.top,
    footerTop
  };
}
